/**
 * Main-menu load-trigger gate (main-menu plan §4; mirrors `src/exploration/gate.ts`, which mirrors
 * `src/editor/gate.ts`). PURE predicate — the ONLY menu-related module mount may import eagerly.
 *
 * The world's `requires` doubles as the load trigger: a world that requires ANY menu registry class
 * pulls the lazy vertical via `await import('../ui/menu/loader')`; every other world never
 * downloads a byte of it. **No world id ever appears in code** — a world DECLARES the menu by
 * placing a `menu_stage`, and this reads the declaration.
 *
 * The class set is DERIVED from the menu sub-table's keys, never written as a literal here
 * (`src/entities/menu/table.ts` is eager-safe: zod schemas + meta + lazy thunks only, already in
 * the main bundle through ENTITY_REGISTRY's spread), so a second menu class arms the trigger with
 * zero edits to this file.
 *
 * ⚠️ THIS IS THE ONE FILE UNDER `src/ui/**` THAT EAGER CODE MAY IMPORT, and it is safe for exactly
 * one reason: it imports NOTHING from the UI foundation. No `strings.ts`, no `theme/tokens`, no
 * `layers.ts`, no DOM, no stylesheet. The markers `scripts/checkBundle.mjs` keeps out of the entry
 * chunk ('ui-layers', 'FFCC00', 'Charge collected') therefore stay out of it. Keep it that way: one
 * convenience import of a string from here puts the whole copy catalog in every player's first
 * download.
 */
import { MENU_ENTITIES } from '../../entities/menu/table';

/**
 * ⚠️ `Object.hasOwn`, NOT `name in MENU_ENTITIES` — AND THE DIFFERENCE IS A REAL OPEN GATE.
 *
 * `in` walks the prototype chain, so a world whose `requires` contained `toString`, `constructor`
 * or `hasOwnProperty` would open this gate and pull the whole menu vertical onto a world that has
 * no menu in it. The set this predicate is defined over is the table's OWN KEYS — which is what
 * "derived from the sub-table's keys" means — and `hasOwn` is the operator that says so.
 *
 * It is caught by a golden (`tests/uiMenu.test.ts`) rather than by review, because the failure is
 * silent in both directions: the wrong operator costs a download nobody attributes to it, and a
 * `requires` naming an unregistered class is refused by the capability gate LATER — after this
 * predicate has already decided. `src/exploration/gate.ts` still spells it `in`; that is a
 * pre-existing hole in a different vertical and is reported rather than edited from here.
 */
export function menuRequired(requires: readonly string[]): boolean {
  return requires.some((name) => Object.hasOwn(MENU_ENTITIES, name));
}
